<script setup lang="ts">
import _cloneDeep from 'lodash/cloneDeep'

type Props = {
  slug: string
  articleTypeSlug: string
  formatString?: string | null
  formatJson?: Record<string, any> | null
  ageLimit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  formatString: null,
  formatJson: null,
  ageLimit: false,
})

const app = useNuxtApp()
const storeUser = useStoreUser()

const sending = ref(false)
const sended = ref(false)
const errorMessage = ref<string | null>(null)
const items = ref<any[] | null>(null)
const ageLimitModel = ref<string[]>([])

const scrollToTop = () => {
  if (import.meta.client) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}

const getEntryStorageKey = () => {
  const userId = storeUser.state.userInfo?.id
  return `im.present.entry.${props.articleTypeSlug}.${props.slug}.${userId}`
}

const loadEntryState = () => {
  if (!import.meta.client) return false
  const key = getEntryStorageKey()
  return localStorage.getItem(key) === '1'
}

const saveEntryState = () => {
  if (!import.meta.client) return
  const key = getEntryStorageKey()
  localStorage.setItem(key, '1')
}

if (import.meta.client) {
  sended.value = loadEntryState()
}

const buildFormat = (rawItems: any[]) => {
  for (let i = 0; i < rawItems.length; i++) {
    const item = rawItems[i]
    const rules = []
    if (item.required === 'false' || item.required === false) {
      item.required = false
    } else {
      item.required = true
      rules.push('required')
    }
    if (item.name === 'post_code') {
      rules.push('zipcode')
    } else {
      if (item['min-length']) {
        rules.push('min:' + item['min-length'])
      } else {
        item['min-length'] = null
      }
      if (item['max-length']) {
        item.counter = item['max-length']
        rules.push('max:' + item['max-length'])
      } else {
        item.counter = undefined
        item['max-length'] = null
      }
    }

    if (rules.length > 0) {
      const rulesString = rules.length ? rules.join('|') : ''
      rawItems[i].rules = rulesString
    } else {
      rawItems[i].rules = ''
    }

    if (item.type === 'radio' || item.type === 'checkbox' || item.type === 'select') {
      item.items = []
      const aryval = item.values.split(',')
      for (let i = 0; i < aryval.length; i++) {
        const val = aryval[i]
        item.items.push({
          label: val,
          value: val,
        })
      }
      item.model = null

      if (item.type === 'radio') {
        item.model = null
      } else if (item.type === 'checkbox') {
        item.model = []
      }
    }
  }
  items.value = rawItems
  console.log('buildFormat', rawItems)
}

const onSubmit = async () => {
  await onToken('')
}

const onToken = async (_token: string) => {
  sending.value = true

  const forms: Record<string, any> = {}
  const currentItems = items.value || []
  for (let i = 0; i < currentItems.length; i++) {
    forms[currentItems[i].name] = currentItems[i].model
    if (Array.isArray(currentItems[i].model)) {
      currentItems[i].confirm = currentItems[i].model.join(',')
    } else {
      currentItems[i].confirm = currentItems[i].model
    }
  }
  const formsJson = JSON.stringify(forms)

  try {
    const res = await app.$ArticleFormApi.articleFormAdd(
      props.slug,
      props.articleTypeSlug,
      formsJson,
    )
    if (res.data.code === 200000) {
      errorMessage.value = null
      sending.value = false
      sended.value = true
      saveEntryState()
      scrollToTop()
      return
    }
    errorMessage.value = res.data.data.message + '(' + res.data.code + ')'
  } catch (e) {
    errorMessage.value = 'エラーが発生しました'
  }

  sending.value = false
}

let rawItems: any[] | null = null
if (props.formatString) {
  rawItems = JSON.parse(props.formatString)
} else if (props.formatJson) {
  rawItems = _cloneDeep(props.formatJson)
}
if (rawItems) {
  buildFormat(rawItems)
}
</script>

<template>
  <div
    v-if="items && !sended"
    class="c-formJson p-form"
  >
    <div class="c-heading_error">
      <template
        v-if="errorMessage"
      >
        {{ errorMessage }}
      </template>
    </div>
    <ValidationForm
      v-slot:default="{ meta }"
    >
      <div class="p-form_controls">
        <template
          v-if="ageLimit"
        >
          <div
            class="c-formJson_inputBlock"
          >
            <div class="p-form_inputBlock_labelRow">
              <div
                class="p-form_inputBlock_labelRow_text is-required"
              >
                16歳未満の方はお申し込みに際し、保護者の同意が必要です。
              </div>
            </div>
            <ValidationCheckBox
              v-model="ageLimitModel"
              name="保護者の同意"
              v-bind:items="[{
                label: '同意あり',
                value: 'agree',
              }]"
              rules="required"
            />
          </div>
        </template>

        <template
          v-for="(item, index) in items"
          v-bind:key="item.name"
        >
          <div
            class="c-formJson_inputBlock"
          >
            <div class="p-form_inputBlock_labelRow">
              <div
                class="p-form_inputBlock_labelRow_text"
                v-bind:class="{ 'is-required': item.required }"
              >
                {{ item.title }}
              </div>
            </div>
            <template
              v-if="item.description"
            >
              <div
                class="p-form_inputBlock_description"
                v-html="item.description"
              />
            </template>
          </div>
          <template
            v-if="item.type === 'text'"
          >
            <ValidationTextField
              v-bind:key="index"
              v-model="item.model"
              v-bind:name="item.title"
              v-bind:placeholder="item.placeholder"
              v-bind:rules="item.rules"
              v-bind:counter="item.counter"
              v-bind:maxlength="item['max-length']"
              variant="outlined"
              density="compact"
              autocomplete="on"
            />
          </template>
          <template
            v-else-if="item.type === 'textarea'"
          >
            <ValidationTextArea
              v-bind:key="index"
              v-model="item.model"
              v-bind:name="item.title"
              v-bind:placeholder="item.placeholder"
              v-bind:rules="item.rules"
              v-bind:counter="item.counter"
              v-bind:maxlength="item['max-length']"
              variant="outlined"
              density="compact"
            />
          </template>
          <template
            v-else-if="item.type === 'select'"
          >
            <ValidationSelect
              v-bind:key="index"
              v-model="item.model"
              v-bind:name="item.title"
              v-bind:items="item.items"
              v-bind:rules="item.rules"
              item-title="label"
              item-value="value"
              variant="outlined"
              density="compact"
            />
          </template>
          <template
            v-else-if="item.type === 'checkbox'"
          >
            <ValidationCheckBox
              v-bind:key="index"
              v-model="item.model"
              v-bind:name="item.title"
              v-bind:items="item.items"
              v-bind:rules="item.rules"
            />
          </template>
          <template
            v-else-if="item.type === 'radio'"
          >
            <ValidationRadio
              v-bind:key="index"
              v-model="item.model"
              v-bind:name="item.title"
              v-bind:items="item.items"
              v-bind:rules="item.rules"
              v-bind:column="false"
            />
          </template>
        </template>
      </div>
      <div
        class="p-form_action"
      >
        <v-btn
          class="c-btn_formSubmit"
          v-bind:disabled="!meta.valid"
          color="primary"
          v-on:click="onSubmit"
        >
          <v-icon>mdi-file-document-edit-outline</v-icon>
          送信
        </v-btn>
      </div>
    </ValidationForm>
  </div>
  <div v-if="sended">
    <div class="c-formJson_h2">
      応募申し込み完了
    </div>
    <p class="c-heading_error">
      応募メールが届かない場合は、応募が出来ていない可能性があります。<br>
      メールアドレスの入力間違いが増えております。メールアドレスを再度ご確認ください。
    </p>
  </div>
</template>

<style>
.c-formJson {
  max-width: 600px;

  &_inputBlock {
    width: 100%;
    max-width: 600px;
    margin-bottom: 0;
  }

  &_complete {
    margin: 30px 0;
  }

  &_h2 {
    margin-top: 30px;
    margin-bottom: 15px;
    font-size: 18px;
  }

  &_confirmBlock {
    display: flex;

    @include mq_down(sp) {
      display: block;
    }

    &_text {
      font-size: 15px;
    }

    &_value {
      font-size: 15px;
    }
  }
}

.c-heading_error {
  justify-content: start !important;
  color: var(--g_color_text_f2423e) !important;
}
</style>
